import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, AuthProvider } from './lib/authContext';
import { supabase } from './lib/supabase';
import SignInModal from './components/SignInModal';
import GeneratorView from './components/GeneratorView';
import { blogPosts } from './components/Blog';
import Admin from './components/Admin';

const FREE_LIMIT = 25;
type Usage = { plan: 'free' | 'paid'; gens_used: number; limit: number; isMaster: boolean; paddle_plan?: string; };
export type Mode = 'feel' | 'expert';

function SignInAutoTrigger({ onSignIn }: { onSignIn: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (new URLSearchParams(location.search).get('signin') === '1') {
      window.history.replaceState({}, '', '/');
      navigate('/', { replace: true });
      onSignIn();
    }
  }, [location.search]);
  return null;
}

function CheckoutSuccessBanner({ onDismiss, fetchUsage }: { onDismiss: () => void; fetchUsage: () => void }) {
  useEffect(() => { fetchUsage(); }, []);
  return (
    <div style={{ background:'linear-gradient(135deg,#c49a3c,#e8b84b)', color:'#fff', textAlign:'center', padding:'14px 24px', fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:'15px', fontWeight:600, display:'flex', alignItems:'center', justifyContent:'center', gap:'12px' }}>
      🎉 Welcome to Pro! Your plan is now active. Start generating prompts below.
      <button onClick={onDismiss} style={{ background:'none', border:'none', color:'#fff', cursor:'pointer', fontSize:'18px', lineHeight:1 }}>×</button>
    </div>
  );
}

function UsageBadge({ usage }: { usage: Usage | null }) {
  if (!usage) return <div className="re-skel" />;
  // All access is free and unlimited. Entitlement infrastructure is preserved
  // (see fetchUsage / Admin), but no quota is surfaced or enforced to the user.
  if (usage.isMaster) return <span className="re-tier-master">★ Master · Unlimited</span>;
  return <span className="re-tier">Free · Unlimited</span>;
}

function Layout({ children, onSignIn, user, loading, usage, signOut }: {
  children: React.ReactNode; onSignIn: () => void; user: any; loading: boolean; usage: Usage | null; signOut: () => void;
}) {
  const navigate = useNavigate();
  const SOCIAL_LINKS = [
    { svg:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="ig-grad" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#f09433"/><stop offset="25%" stopColor="#e6683c"/><stop offset="50%" stopColor="#dc2743"/><stop offset="75%" stopColor="#cc2366"/><stop offset="100%" stopColor="#bc1888"/></linearGradient></defs><rect width="24" height="24" rx="6" fill="url(#ig-grad)"/><rect x="5.5" y="5.5" width="13" height="13" rx="4" stroke="white" strokeWidth="1.5" fill="none"/><circle cx="12" cy="12" r="3.2" stroke="white" strokeWidth="1.5" fill="none"/><circle cx="17" cy="7" r="1" fill="white"/></svg>, label:'Instagram', href:'https://www.instagram.com/raagengine?igsh=MWtydzJscTB1NXhzcQ==', color:'#E1306C' },
    { svg:<svg width="16" height="16" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>, label:'Facebook',  href:'https://www.facebook.com/61572010824212/', color:'#1877F2' },
    { svg:<svg width="16" height="16" viewBox="0 0 24 24" fill="#FF0000"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>, label:'YouTube',   href:'https://www.youtube.com/@raagenginestudio', color:'#FF0000' },
  ];
  const footerCols = [
    { h:'Platforms', links:[['Suno AI','/suno'],['Udio','/udio'],['Stable Audio','/stable-audio'],['Mureka','/mureka'],['Beatoven','/beatoven'],['MusicGen','/musicgen'],['Soundraw','/soundraw'],['Loudly','/loudly']] },
    { h:'Genres', links:[['Sleep Music','/sleep-music'],['Hip Hop','/hiphop'],['Pop','/pop'],['Ambient','/ambient'],['Lo-Fi','/lofi'],['Indian Classical','/indian-classical-music-ai'],['Cinematic','/cinematic'],['Sad Music','/sad-music']] },
    { h:'Guides', links:[['AI Prompt Generator','/ai-music-prompt-generator'],['Royalty Free Music','/royalty-free-ai-music'],['AI Music for YouTube','/ai-music-for-youtube'],['Best AI Music 2026','/best-ai-music-generator-2026'],['Suno AI Pricing','/suno-ai-pricing'],['ChatGPT Music','/chatgpt-music-prompts'],['AI Music for Games','/ai-music-for-games'],['AI for Podcasts','/ai-music-for-podcasts']] },
    { h:'Creators', links:[['TikTok Music','/tiktok-music'],['YouTube Shorts','/youtube-shorts'],['YouTube Channels','/youtube-music'],['Feel Mode','/feel-mode'],['Free Tools','/free-ai-music-generator'],['Suno vs Udio','/suno-vs-udio'],['How to Use Suno','/how-to-use-suno-ai'],['Make Money with Suno','/how-to-make-money-suno-ai']] },
    { h:'Company', links:[['About','/about/'],['Blog','/blog/'],['Pricing','/pricing'],['Shop','/shop'],['Contact','/contact'],['Privacy','/privacy'],['Terms','/terms'],['All Tools','/all-tools']] },
  ];
  return (
    <div className="re-app">
      <div className="re-topbar" />
      <div className="re-container" style={{ maxWidth:'1200px', margin:'0 auto' }}>
        <header className="re-header" style={{ background:'#faf7f2', borderBottom:'1px solid rgba(196,154,60,0.18)', padding:'0 clamp(14px,3vw,48px)', display:'flex', alignItems:'center', justifyContent:'space-between', height:'clamp(56px,8vw,68px)', position:'sticky', top:0, zIndex:100, boxShadow:'0 1px 0 rgba(196,154,60,0.12)', gap:'8px' }}>
          <div className="re-logo-wrap" onClick={() => navigate('/')} style={{ cursor:'pointer', display:'flex', alignItems:'center', gap:'8px', flexShrink:0, minWidth:0 }}>
            <img src="/raagengine-logo.webp" alt="RaagEngine" style={{ width:'clamp(32px,5vw,44px)', height:'clamp(32px,5vw,44px)', borderRadius:'50%', flexShrink:0 }} />
            <div style={{ display:'flex', flexDirection:'column', gap:'1px', minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'baseline', gap:'6px' }}>
                <span style={{ fontFamily:"'Cormorant Garamond',Georgia,serif", fontSize:'clamp(17px,4vw,24px)', fontWeight:700, color:'#1a1208', lineHeight:1, letterSpacing:'-0.3px', whiteSpace:'nowrap' }}>RaagEngine</span>
                <span className="re-nav-desktop" style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:'8px', fontWeight:700, letterSpacing:'3px', textTransform:'uppercase', color:'#c49a3c' }}>Studio</span>
              </div>
              <div className="re-nav-desktop" style={{ fontSize:'10px', color:'#8a7055', fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:'nowrap' }}>AI Music Prompt Generator · Suno AI · Udio · 6 more</div>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'clamp(8px,2vw,20px)', flexShrink:0 }}>
            <a href="/blog/" className="re-nav-link re-nav-desktop" style={{ fontSize:'14px', fontWeight:500, color:'#6b5c3e', fontFamily:"'Plus Jakarta Sans',sans-serif", textDecoration:'none', whiteSpace:'nowrap' }}>Blog</a>
            <a href="/pricing/" className="re-nav-link re-nav-desktop" style={{ fontSize:'14px', fontWeight:500, color:'#6b5c3e', textDecoration:'none', fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:'nowrap' }}>Pricing</a>
            <a href="/shop/" className="re-nav-link re-nav-desktop" style={{ fontSize:'14px', fontWeight:500, color:'#6b5c3e', textDecoration:'none', fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:'nowrap' }}>Shop</a>
            {loading ? <div className="re-skel" style={{ width:72 }} /> : user ? (
              <>
                <UsageBadge usage={usage} />
                <span className="re-email re-nav-desktop" style={{ fontSize:'11px', color:'#6b5c3e', maxWidth:130, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user.email}</span>
                <button className="re-btn-ghost re-nav-desktop" onClick={signOut}>Sign out</button>
              </>
            ) : (
              <>
                <button className="re-btn-signin" onClick={onSignIn} style={{ whiteSpace:'nowrap', padding:'6px 12px', fontSize:'12px' }}>
                  👤 Sign in<span className="re-free-tag re-nav-desktop" style={{ marginLeft:6 }}>free</span>
                </button>
              </>
            )}
          </div>
        </header>
        {children}
        {/* ── Rich multi-column footer ── */}
        <footer style={{ background:'#2c1810', marginTop:'48px', padding:'56px clamp(16px,4vw,24px) 0' }}>
          <div className="re-footer-grid" style={{ maxWidth:'1200px', margin:'0 auto', display:'grid', gridTemplateColumns:'1.5fr repeat(5,1fr)', gap:'40px', paddingBottom:'48px', borderBottom:'1px solid rgba(255,255,255,0.1)', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            <div className="re-footer-brand">
              <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'12px' }}>
                <img src="/raagengine-logo.webp" alt="" style={{ width:28, height:28, borderRadius:'50%' }} />
                <span style={{ fontFamily:"'Cormorant Garamond',Georgia,serif", fontSize:'18px', fontWeight:700, color:'#faf7f2' }}>RaagEngine</span>
              </div>
              <p style={{ fontSize:'13px', color:'rgba(255,255,255,0.6)', lineHeight:1.7, maxWidth:'200px' }}>AI music prompts for every platform and every feeling.</p>
              <div className="re-footer-social" style={{ display:'flex', gap:'10px', marginTop:'16px' }}>
                {SOCIAL_LINKS.map(s => (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                    style={{ width:'36px', height:'36px', borderRadius:'50%', background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', display:'flex', alignItems:'center', justifyContent:'center', textDecoration:'none', transition:'background .2s' }}
                    onMouseOver={e=>(e.currentTarget.style.background=s.color+'40')}
                    onMouseOut={e=>(e.currentTarget.style.background='rgba(255,255,255,0.08)')}>
                    {s.svg}
                  </a>
                ))}
              </div>
            </div>
            {footerCols.map((col) => (
              <div key={col.h} className={col.h === 'Company' ? 're-footer-col-company' : ''}>
                <div style={{ fontSize:'11px', fontWeight:700, letterSpacing:'0.14em', textTransform:'uppercase', color:'rgba(255,255,255,0.6)', marginBottom:'14px' }}>{col.h}</div>
                {col.links.map(([label, href]) => (
                  <a key={label} href={href} style={{ display:'block', color:'rgba(255,255,255,0.65)', fontSize:'13px', textDecoration:'none', marginBottom:'8px', transition:'color .2s' }}
                      onMouseOver={e => (e.currentTarget.style.color='#c49a3c')}
                      onMouseOut={e => (e.currentTarget.style.color='rgba(255,255,255,0.65)')}
                    >{label}</a>
                ))}
              </div>
            ))}
          </div>
          <div className="re-footer-bottom" style={{ maxWidth:'1200px', margin:'0 auto', padding:'20px 0', fontSize:'12px', color:'rgba(255,255,255,0.6)', fontFamily:"'Plus Jakarta Sans',sans-serif", display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'8px' }}>
            <span>© 2026 RaagEngine · AI Music Prompt Generator for Suno AI, Udio &amp; more</span>
            <span style={{ display:'flex', gap:'16px' }}>
              <a href="/privacy/" style={{ color:'rgba(255,255,255,0.6)', textDecoration:'none' }}>Privacy</a>
              <a href="/terms/" style={{ color:'rgba(255,255,255,0.6)', textDecoration:'none' }}>Terms</a>
              <a href="/contact/" style={{ color:'rgba(255,255,255,0.6)', textDecoration:'none' }}>Contact</a>
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
}

// ── Hero banner (unchanged) ──────────────────────────────────
const SLIDES = [
  { kicker:'YOUTUBE MUSIC CHANNELS', heading:'Turn AI music into a YouTube income stream', sub:'SEO titles, descriptions, thumbnails — everything you need to grow and monetise.', cta:'Start generating free →' },
  { kicker:'SUNO AI PROMPTS', heading:'The most specific Suno prompts on the internet', sub:'35+ Indian ragas · 200+ instruments · BPM, reverb, arrangement — copy-paste ready.', cta:'Generate a Suno prompt →' },
  { kicker:'35+ INDIAN RAGAS', heading:'Indian Classical music AI — finally done right', sub:'Raag Yaman, Bhairavi, Darbari Kanada — real raga science turned into AI prompts.', cta:'Explore ragas →' },
  { kicker:'8 PLATFORMS IN ONE CLICK', heading:'One input. Suno, Udio, Stable Audio and 5 more.', sub:'Stop copying and reformatting. RaagEngine generates for every platform at once.', cta:'Try all 8 platforms →' },
  { kicker:'EVERY TRACK = INCOME', heading:'Every track you generate is a potential income stream', sub:'YouTube AdSense · Spotify royalties · DistroKid metadata · Social captions — all included.', cta:'See how it works →' },
];

function HeroBanner({ onSignIn, user, onNavigateGenerate }: { onSignIn: () => void; user: any; onNavigateGenerate: () => void }) {
  const [idx, setIdx] = useState(0);
  const [fade, setFade] = useState(true);
  useEffect(() => {
    const t = setInterval(() => {
      setFade(false);
      setTimeout(() => { setIdx(i => (i + 1) % SLIDES.length); setFade(true); }, 350);
    }, 5500);
    return () => clearInterval(t);
  }, []);
  const s = SLIDES[idx];
  return (
    <div className="re-hero" style={{ position:'relative', background:'linear-gradient(135deg,#231508 0%,#3a2810 50%,#231508 100%)', borderRadius:'16px', overflow:'hidden', marginBottom:'32px', minHeight:'220px', display:'flex', alignItems:'center' }}>
      <div style={{ position:'absolute', inset:0, background:'radial-gradient(ellipse at 75% 50%,rgba(196,154,60,0.18) 0%,rgba(196,154,60,0.06) 40%,transparent 70%)' }} />
      <div style={{ position:'absolute', right:'32px', top:'50%', transform:'translateY(-50%)', opacity:0.06, fontSize:'clamp(80px,15vw,180px)', fontFamily:"'Cormorant Garamond',Georgia,serif", fontWeight:700, color:'#c49a3c', userSelect:'none', lineHeight:1 }}>♩</div>
      <div style={{ position:'relative', zIndex:1, padding:'clamp(24px,4vw,48px) clamp(24px,4vw,56px)', maxWidth:'620px', opacity:fade ? 1 : 0, transition:'opacity 0.35s ease' }}>
        <div style={{ fontSize:'10px', fontWeight:700, letterSpacing:'2px', color:'#c49a3c', marginBottom:'10px', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{s.kicker}</div>
        <h1 style={{ fontSize:'clamp(20px,3vw,32px)', fontWeight:700, color:'#fff', fontFamily:"'Cormorant Garamond',Georgia,serif", lineHeight:1.2, marginBottom:'10px' }}>{s.heading}</h1>
        <p style={{ fontSize:'14px', color:'rgba(255,255,255,0.82)', lineHeight:1.6, marginBottom:'20px', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{s.sub}</p>
        <button onClick={user ? onNavigateGenerate : onSignIn} style={{ background:'linear-gradient(135deg,#c49a3c,#e8b84b)', color:'#1a1208', border:'none', borderRadius:'24px', padding:'10px 22px', fontSize:'13px', fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
          {user ? '✦ Generate now →' : s.cta}
        </button>
      </div>
      <div style={{ position:'absolute', bottom:'14px', left:'clamp(24px,4vw,56px)', display:'flex', gap:'6px' }}>
        {SLIDES.map((_,i) => (
          <button key={i} onClick={() => { setFade(false); setTimeout(()=>{ setIdx(i); setFade(true); },350); }}
            style={{ width:i===idx?18:6, height:6, borderRadius:3, background:i===idx?'#c49a3c':'rgba(255,255,255,0.3)', border:'none', cursor:'pointer', padding:0, transition:'all 0.3s' }} />
        ))}
      </div>
    </div>
  );
}

// ── Multilingual Instructions ─────────────────────────────────────────────────
const INSTRUCTIONS: Record<string, { feel: string[]; expert: string[]; dismiss: string; heading: string }> = {
  en: {
    heading: 'How to use this generator',
    feel: ['Choose a Genre (Pop, Lo-Fi, Classical, Global…)', 'Pick a Region for cultural flavour (optional)', 'Select a Mood or Scene that matches your feeling', 'Choose what people will do while listening', 'Set intensity with the Calm → Intense slider', 'Select your platforms and click Generate'],
    expert: ['Choose a Raga or Scale from the dropdown', 'Pick Instrument A (lead), B (harmony), C (texture)', 'Set BPM, Reverb, and Arrangement style', 'Add Binaural frequency for meditation music', 'Select your platforms and click Generate Full Package'],
    dismiss: 'Got it',
  },
  hi: {
    heading: 'Generator कैसे use करें',
    feel: ['Genre चुनें (Pop, Lo-Fi, Classical, Global…)', 'Region चुनें — cultural flavour के लिए (optional)', 'अपनी Feeling से match करता Mood या Scene select करें', 'Listeners क्या करेंगे? वो activity चुनें', 'Calm → Intense slider से intensity set करें', 'Platforms select करें और Generate दबाएं'],
    expert: ['Dropdown से Raag या Scale चुनें', 'Instrument A (lead), B (harmony), C (texture) चुनें', 'BPM, Reverb और Arrangement style set करें', 'Meditation music के लिए Binaural frequency add करें', 'Platforms select करें और Generate Full Package दबाएं'],
    dismiss: 'समझ गया',
  },
  es: {
    heading: 'Cómo usar este generador',
    feel: ['Elige un Género (Pop, Lo-Fi, Clásico, Global…)', 'Selecciona una Región para sabor cultural (opcional)', 'Elige el Mood o Escena que refleja tu sentimiento', '¿Qué harán los oyentes? Selecciona esa actividad', 'Ajusta la intensidad con el slider Calma → Intenso', 'Selecciona plataformas y haz clic en Generar'],
    expert: ['Elige Raag o Escala del menú desplegable', 'Instrumento A (melodía), B (armonía), C (textura)', 'Configura BPM, Reverb y estilo de arreglo', 'Agrega frecuencia Binaural para meditación', 'Selecciona plataformas y haz clic en Generar'],
    dismiss: 'Entendido',
  },
  pt: {
    heading: 'Como usar este gerador',
    feel: ['Escolha um Gênero (Pop, Lo-Fi, Clássico, Global…)', 'Selecione uma Região para sabor cultural (opcional)', 'Escolha o Humor ou Cena que representa seu sentimento', 'O que as pessoas farão ao ouvir? Escolha a atividade', 'Ajuste a intensidade com o slider Calmo → Intenso', 'Selecione plataformas e clique em Gerar'],
    expert: ['Escolha Raag ou Escala no menu dropdown', 'Instrumento A (melodia), B (harmonia), C (textura)', 'Configure BPM, Reverb e estilo de arranjo', 'Adicione frequência Binaural para meditação', 'Selecione plataformas e clique em Gerar'],
    dismiss: 'Entendi',
  },
  fr: {
    heading: 'Comment utiliser ce générateur',
    feel: ['Choisissez un Genre (Pop, Lo-Fi, Classique…)', 'Sélectionnez une Région pour la couleur culturelle (optionnel)', 'Choisissez le Mood ou la Scène qui correspond', 'Que feront les auditeurs ? Choisissez l\'activité', 'Réglez l\'intensité avec le slider Calme → Intense', 'Sélectionnez les plateformes et cliquez Générer'],
    expert: ['Choisissez Raag ou Gamme dans le menu', 'Instrument A (mélodie), B (harmonie), C (texture)', 'Configurez BPM, Reverb et style d\'arrangement', 'Ajoutez une fréquence Binaurale pour la méditation', 'Sélectionnez les plateformes et cliquez Générer'],
    dismiss: 'Compris',
  },
  de: {
    heading: 'Wie du diesen Generator benutzt',
    feel: ['Wähle ein Genre (Pop, Lo-Fi, Klassik, Global…)', 'Region für kulturellen Flair wählen (optional)', 'Stimmung oder Szene passend zu deinem Gefühl wählen', 'Was werden die Zuhörer tun? Aktivität auswählen', 'Intensität mit dem Ruhig → Intensiv-Slider einstellen', 'Plattformen auswählen und auf Generieren klicken'],
    expert: ['Raag oder Tonleiter aus Dropdown wählen', 'Instrument A (Melodie), B (Harmonie), C (Textur)', 'BPM, Hall und Arrangement-Stil einstellen', 'Binaural-Frequenz für Meditationsmusik hinzufügen', 'Plattformen wählen und auf Generieren klicken'],
    dismiss: 'Verstanden',
  },
  ja: {
    heading: 'ジェネレーターの使い方',
    feel: ['ジャンルを選ぶ（ポップ、ローファイ、クラシック…）', '文化的なフレーバーのために地域を選ぶ（任意）', '気分や場面に合ったムードを選ぶ', '聴きながら何をする？その活動を選ぶ', 'スライダーで強度を調整（穏やか→強烈）', 'プラットフォームを選んでGenerateをクリック'],
    expert: ['ドロップダウンからラーガまたはスケールを選ぶ', '楽器A（リード）、B（ハーモニー）、C（テクスチャ）', 'BPM、リバーブ、アレンジスタイルを設定', '瞑想音楽用にバイノーラル周波数を追加', 'プラットフォームを選んでGenerateをクリック'],
    dismiss: 'わかった',
  },
  ar: {
    heading: 'كيفية استخدام هذا المولّد',
    feel: ['اختر النوع الموسيقي (بوب، لو-فاي، كلاسيك…)', 'اختر المنطقة للنكهة الثقافية (اختياري)', 'اختر الحالة المزاجية التي تعبر عن شعورك', 'ماذا سيفعل المستمعون؟ اختر النشاط', 'اضبط الشدة بالشريط المنزلق هادئ → مكثف', 'اختر المنصات ثم اضغط توليد'],
    expert: ['اختر الراغ أو السلم من القائمة', 'آلة A (لحن)، B (تناغم)، C (نسيج صوتي)', 'اضبط BPM والصدى وأسلوب التوزيع', 'أضف تردد ثنائي الأذن للموسيقى التأملية', 'اختر المنصات ثم اضغط توليد'],
    dismiss: 'فهمت',
  },
  ko: {
    heading: '생성기 사용 방법',
    feel: ['장르를 선택하세요 (팝, 로파이, 클래식…)', '문화적 색채를 위한 지역 선택 (선택사항)', '감정에 맞는 분위기나 장면을 선택하세요', '청취자들이 무엇을 할까요? 활동을 선택하세요', '슬라이더로 강도 조절 (조용함 → 강렬함)', '플랫폼 선택 후 생성 버튼 클릭'],
    expert: ['드롭다운에서 라그 또는 스케일 선택', '악기 A (리드), B (화음), C (텍스처) 선택', 'BPM, 리버브, 편곡 스타일 설정', '명상 음악을 위한 바이노럴 주파수 추가', '플랫폼 선택 후 전체 패키지 생성 클릭'],
    dismiss: '알겠습니다',
  },
};

function getLang(): string {
  const lang = (navigator.language || 'en').toLowerCase().split('-')[0];
  return INSTRUCTIONS[lang] ? lang : 'en';
}

function InstructionBlock({ mode, onDismiss }: { mode: Mode; onDismiss: () => void }) {
  const lang = getLang();
  const t = INSTRUCTIONS[lang];
  const steps = mode === 'feel' ? t.feel : t.expert;
  const isRTL = lang === 'ar';
  return (
    <div style={{
      background: 'linear-gradient(135deg,#fdf9f0,#faf4e8)',
      border: '1.5px solid rgba(196,154,60,0.3)',
      borderRadius: '14px',
      padding: '18px 20px',
      marginBottom: '18px',
      fontFamily: "'Plus Jakarta Sans',sans-serif",
      direction: isRTL ? 'rtl' : 'ltr',
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
        <span style={{ fontSize:'11px', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'#c49a3c' }}>
          {t.heading}
        </span>
        <button onClick={onDismiss} style={{ background:'none', border:'1px solid rgba(196,154,60,0.4)', borderRadius:'6px', padding:'3px 12px', fontSize:'11px', fontWeight:600, color:'#a07840', cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
          {t.dismiss} ✓
        </button>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:'7px' }}>
        {steps.map((step, i) => (
          <div key={i} style={{ display:'flex', gap:'7px', alignItems:'flex-start', fontSize:'12px', color:'#3a2810', lineHeight:1.5 }}>
            <span style={{ background:'rgba(196,154,60,0.15)', color:'#a07840', fontWeight:700, borderRadius:'50%', width:'18px', height:'18px', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:'10px' }}>{i+1}</span>
            <span>{step}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Bilingual section labels shown above generator ────────────────────────────
const SECTION_LABELS: Record<string, Record<string, string>> = {
  genre:      { en:'Genre', hi:'Genre / शैली', es:'Género', pt:'Gênero', fr:'Genre', de:'Genre', ja:'ジャンル', ar:'النوع / Genre', ko:'장르' },
  mood:       { en:'Mood & Scene', hi:'Mood & Scene / भाव', es:'Estado de ánimo', pt:'Humor', fr:'Humeur', de:'Stimmung', ja:'ムード', ar:'المزاج / Mood', ko:'무드' },
  activity:   { en:'Activity', hi:'Activity / गतिविधि', es:'Actividad', pt:'Atividade', fr:'Activité', de:'Aktivität', ja:'アクティビティ', ar:'النشاط / Activity', ko:'활동' },
  intensity:  { en:'Calm → Intense', hi:'शांत → तीव्र (Calm → Intense)', es:'Calma → Intenso', pt:'Calmo → Intenso', fr:'Calme → Intense', de:'Ruhig → Intensiv', ja:'穏やか → 強烈', ar:'هادئ → مكثّف', ko:'조용 → 강렬' },
  platforms:  { en:'Generate prompts for:', hi:'इन Platforms के लिए Prompt बनाएं:', es:'Generar para:', pt:'Gerar para:', fr:'Générer pour:', de:'Generieren für:', ja:'プラットフォームを選択:', ar:'توليد لـ:', ko:'플랫폼 선택:' },
  raga:       { en:'Raga / Scale', hi:'Raag / Scale / राग', es:'Raga / Escala', pt:'Raga / Escala', fr:'Raga / Gamme', de:'Raga / Tonleiter', ja:'ラーガ / スケール', ar:'الراغ / السلم', ko:'라가 / 스케일' },
  instruments:{ en:'Instruments', hi:'Instruments / वाद्य यंत्र', es:'Instrumentos', pt:'Instrumentos', fr:'Instruments', de:'Instrumente', ja:'楽器', ar:'الآلات الموسيقية', ko:'악기' },
  bpm:        { en:'BPM (Speed)', hi:'BPM (Speed / गति)', es:'BPM (Velocidad)', pt:'BPM (Velocidade)', fr:'BPM (Vitesse)', de:'BPM (Tempo)', ja:'BPM（速度）', ar:'BPM (السرعة)', ko:'BPM (속도)' },
  reverb:     { en:'Reverb (Echo)', hi:'Reverb (Echo / गूंज)', es:'Reverb (Eco)', pt:'Reverb (Eco)', fr:'Reverb (Écho)', de:'Reverb (Hall)', ja:'リバーブ（エコー）', ar:'Reverb (الصدى)', ko:'리버브 (에코)' },
  binaural:   { en:'Binaural Beats', hi:'Binaural Beats / द्विनादी', es:'Latidos Binaurales', pt:'Batidas Binaurais', fr:'Battements Binauraux', de:'Binaural Beats', ja:'バイノーラルビート', ar:'نبضات ثنائية الأذن', ko:'바이노럴 비트' },
};

function BilingualLabels({ mode }: { mode: Mode }) {
  const lang = getLang();
  if (lang === 'en') return null; // English users see normal labels, no duplication needed
  const L = (key: string) => SECTION_LABELS[key]?.[lang] || SECTION_LABELS[key]?.en || key;
  const isRTL = lang === 'ar';
  const pill = (label: string) => (
    <span style={{
      display:'inline-block',
      background:'rgba(196,154,60,0.1)',
      border:'1px solid rgba(196,154,60,0.25)',
      borderRadius:'20px',
      padding:'3px 12px',
      fontSize:'11px',
      fontWeight:600,
      color:'#a07840',
      fontFamily:"'Plus Jakarta Sans',sans-serif",
      whiteSpace:'nowrap',
    }}>{label}</span>
  );
  const feelLabels = [L('genre'), L('mood'), L('activity'), L('intensity'), L('platforms')];
  const expertLabels = [L('raga'), L('instruments'), L('bpm'), L('reverb'), L('binaural'), L('platforms')];
  const labels = mode === 'feel' ? feelLabels : expertLabels;
  return (
    <div style={{
      display:'flex',
      flexWrap:'wrap',
      gap:'6px',
      marginBottom:'12px',
      direction: isRTL ? 'rtl' : 'ltr',
      alignItems:'center',
    }}>
      <span style={{ fontSize:'10px', fontWeight:700, color:'#c49a3c', letterSpacing:'0.1em', textTransform:'uppercase', fontFamily:"'Plus Jakarta Sans',sans-serif", marginRight:'4px' }}>
        {lang === 'hi' ? 'फ़ील्ड:' : lang === 'es' ? 'Campos:' : lang === 'pt' ? 'Campos:' : lang === 'fr' ? 'Champs:' : lang === 'de' ? 'Felder:' : lang === 'ja' ? 'フィールド:' : lang === 'ar' ? 'الحقول:' : lang === 'ko' ? '필드:' : 'Fields:'}
      </span>
      {labels.map((l, i) => <span key={i}>{pill(l)}</span>)}
    </div>
  );
}

// ── YouTube Channel Config ────────────────────────────────────────────────────
// Channel handle (for subscribe link) and Channel ID (for RSS auto-populate).
// To find your Channel ID: YouTube Studio → Settings → Channel → Advanced settings → Channel ID
const YT_CHANNEL_HANDLE = 'https://www.youtube.com/@raagenginestudio';
const YT_CHANNEL_ID = 'UCsPNt0taKw1n5bBkRKZz0PA';

// Pinned videos: the 5 you want featured. Titles/thumbnails auto-fetch from YouTube.
const YT_PINNED_IDS = [
  '6vK44kchSwk',
  'spgbhPmWOLg',
  'L8qDaABN_8g',
  'jFAvcIsPDKA',
  'yTtLQt1eTpA',
];


// ── Platform tiles — real favicons via Google S2 service ─────────────────────
const PLATFORMS = [
  { name:'Suno AI',      href:'/suno',                badge:'#7c3aed', sub:'Style tags',           domain:'suno.com' },
  { name:'Udio',         href:'/udio',                badge:'#0ea5e9', sub:'Natural language',      domain:'udio.com' },
  { name:'Stable Audio', href:'/stable-audio',        badge:'#ea580c', sub:'Detailed prompts',      domain:'stability.ai' },
  { name:'Mureka',       href:'/mureka',              badge:'#16a34a', sub:'Structured fields',     domain:'mureka.ai' },
  { name:'Beatoven.ai',  href:'/beatoven',            badge:'#db2777', sub:'Mood + scene',          domain:'beatoven.ai' },
  { name:'Soundraw',     href:'/soundraw',            badge:'#0891b2', sub:'Editable loops',        domain:'soundraw.io' },
  { name:'Loudly',       href:'/loudly',              badge:'#d97706', sub:'Loop music',            domain:'loudly.com' },
  { name:'MusicGen',     href:'/musicgen',            badge:'#6366f1', sub:'Meta AI · Open source', domain:'ai.meta.com' },
];

function PlatformLogo({ domain, badge, size = 36 }: { domain: string; badge: string; size?: number }) {
  const [err, setErr] = useState(false);
  const src = `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  if (err) {
    const initials = domain.split('.')[0].substring(0,2).toUpperCase();
    return (
      <div style={{ width:size, height:size, borderRadius:'10px', background:badge, display:'flex', alignItems:'center', justifyContent:'center', fontSize:size*0.35, fontWeight:800, color:'white', fontFamily:'Arial,sans-serif' }}>
        {initials}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={domain}
      width={size}
      height={size}
      onError={() => setErr(true)}
      style={{ width:size, height:size, borderRadius:'10px', objectFit:'contain', background:'transparent' }}
    />
  );
}

// ── YouTube hook: oEmbed title fetch + RSS channel auto-populate ──────────────
interface YTVideo { id: string; title: string; thumbnail: string; }

function useYouTubeVideos(): { videos: YTVideo[]; loading: boolean } {
  const [videos, setVideos] = useState<YTVideo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchAll() {
      // Step 1: start with pinned videos, fetch their titles via oEmbed
      const pinned = await Promise.all(
        YT_PINNED_IDS.map(async (id): Promise<YTVideo> => {
          const thumb = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
          try {
            const r = await fetch(
              `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${id}&format=json`,
              { signal: AbortSignal.timeout(4000) }
            );
            if (r.ok) {
              const d = await r.json();
              return { id, title: d.title ?? id, thumbnail: thumb };
            }
          } catch { /* oEmbed blocked by CORS in some envs — fall through */ }
          return { id, title: '', thumbnail: thumb };
        })
      );

      // Step 2: if a channel ID is configured, also pull latest from RSS feed
      // RSS is public, no API key needed. We fetch via allorigins proxy to avoid CORS.
      let rssVideos: YTVideo[] = [];
      if (YT_CHANNEL_ID && !YT_CHANNEL_ID.startsWith('REPLACE')) {
        try {
          const rssUrl = `https://www.youtube.com/feeds/videos.xml?channel_id=${YT_CHANNEL_ID}`;
          const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(rssUrl)}`;
          const r = await fetch(proxyUrl, { signal: AbortSignal.timeout(5000) });
          if (r.ok) {
            const json = await r.json();
            const parser = new DOMParser();
            const xml = parser.parseFromString(json.contents, 'text/xml');
            const entries = Array.from(xml.querySelectorAll('entry')).slice(0, 10);
            rssVideos = entries.map(e => {
              const id = e.querySelector('videoId')?.textContent ?? '';
              const title = e.querySelector('title')?.textContent ?? '';
              return { id, title, thumbnail: `https://i.ytimg.com/vi/${id}/hqdefault.jpg` };
            }).filter(v => v.id && !YT_PINNED_IDS.includes(v.id));
          }
        } catch { /* RSS unavailable — continue with pinned only */ }
      }

      // Merge: pinned first, then any new RSS-only videos (latest channel uploads)
      const seen = new Set(pinned.map(v => v.id));
      const merged = [
        ...pinned,
        ...rssVideos.filter(v => !seen.has(v.id)),
      ].slice(0, 6); // max 6 on homepage

      setVideos(merged);
      setLoading(false);
    }
    fetchAll();
  }, []);

  return { videos, loading };
}

// ── FAQ data (drives both UI and JSON-LD schema) ──────────────────────────────
const FAQ_ITEMS = [
  { q: 'What is RaagEngine?', a: 'RaagEngine is an AI music prompt generator that creates production-ready prompts for Suno AI, Udio, Stable Audio, Mureka, Beatoven, Soundraw, Loudly, and MusicGen — all from a single input. It specialises in Indian classical ragas, world music traditions, and YouTube SEO metadata.' },
  { q: 'Is RaagEngine free to use?', a: 'Yes — RaagEngine is completely free. Unlimited prompt generations, all 8 platforms, no credit card, no subscription. Just sign in with Google or email and start generating.' },
  { q: 'Which AI music platforms does RaagEngine support?', a: 'RaagEngine generates optimised prompts for 8 platforms simultaneously: Suno AI, Udio, Stable Audio, Mureka, Beatoven.ai, Soundraw, Loudly, and Meta MusicGen.' },
  { q: 'What makes RaagEngine different from other prompt generators?', a: 'Three things: (1) Indian classical music depth — 35+ ragas, 200+ instruments, authentic raga science. (2) YouTube SEO — every generation includes an SEO-optimised title, description, and thumbnail concept. (3) All 8 major platforms in one click, no reformatting needed.' },
  { q: 'Can I use AI-generated music on YouTube without copyright issues?', a: 'Music generated via Suno AI or Udio using RaagEngine prompts is created fresh — it is not sampled from existing recordings. Most platforms grant you rights to the output. Always verify the specific platform\'s terms. RaagEngine\'s blog covers this in detail.' },
  { q: 'Does RaagEngine support languages other than English?', a: 'The generator UI supports English, Hindi (हिंदी), Spanish, Portuguese, French, German, Japanese, Arabic, and Korean. Blog content is now available in Hindi, French, Spanish, Persian (Farsi), Japanese, and Arabic — covering Indian raags, French modes, flamenco, Iranian dastgah, Japanese scales, and Arabic maqam.' },
];

function FAQSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
}

// ── MusicLinksSection: 4 items + expandable ───────────────────────────────────
function MusicLinksSection({ navigate }: { navigate: (p:string)=>void }) {
  const [stylesOpen, setStylesOpen] = useState(false);
  const [creatorsOpen, setCreatorsOpen] = useState(false);
  const F = "'Plus Jakarta Sans',sans-serif";
  const ls = { display:'flex', alignItems:'flex-start', gap:'8px', padding:'clamp(8px,1.5vw,13px) clamp(10px,2vw,16px)', background:'#fff', border:'1px solid rgba(196,154,60,0.15)', borderRadius:'10px', textDecoration:'none', marginBottom:'8px' } as const;

  const STYLES_ALL = [
    ['🎧','Lo-Fi Music Generator','Study beats, chill, rainy day','/lofi'],
    ['🪔','Meditation Music Generator','Binaural, 432Hz, brainwave','/meditation'],
    ['🏛','Indian Classical Music AI','35+ ragas, Sitar, Tabla, Bansuri','/indian-classical-music-ai'],
    ['🎬','Cinematic Music Generator','Epic orchestral, film scores','/cinematic'],
    ['😴','Sleep Music Generator','Delta waves, Tibetan bowls','/sleep-music'],
    ['🎤','Hip Hop Generator','Trap, boom bap, UK drill','/hiphop'],
    ['🎵','Pop Song Generator','Electropop, indie, K-pop','/pop'],
    ['🌙','Ambient Generator','Focus, study, meditation','/ambient'],
    ['😔','Sad Music Generator','Melancholic and emotional','/sad-music'],
    ['🎷','Jazz Music Generator','Bebop, bossa nova, smooth jazz','/suno'],
    ['🌊','Chill Music Generator','Downtempo, chillwave, café','/suno'],
    ['⚡','Trance Music Generator','Progressive, psytrance, Goa','/suno'],
  ];
  const CREATORS_ALL = [
    ['▶','YouTube Music Channel','AI music + SEO + monetisation','/youtube-music'],
    ['⚡','TikTok Music Generator','Copyright-free for TikTok','/tiktok-music'],
    ['📹','YouTube Shorts Music','60-second optimised tracks','/youtube-shorts'],
    ['📦','DistroKid Distribution Guide','Get AI music on Spotify','/distrokid'],
    ['🆚','Suno vs Udio 2026','Which platform is better?','/suno-vs-udio'],
    ['🆓','Best Free AI Music Tools','Compare free generators','/free-ai-music-generator'],
    ['🛠️','Feel Mode','Emotion → music, no theory needed','/feel-mode'],
    ['📝','Blog — AI Music Guides','How to grow a music channel','/blog/'],
    ['💰','Pricing','100% free · unlimited generations','/pricing'],
    ['🎛️','AI Music Prompt Generator','Best tool for Suno prompts','/ai-music-prompt-generator'],
    ['©️','Royalty Free AI Music','Copyright-free music guide','/royalty-free-ai-music'],
    ['🏆','Best AI Music 2026','Full platform comparison','/best-ai-music-generator-2026'],
    ['🎬','AI Music for YouTube','Build a music channel','/ai-music-for-youtube'],
    ['💳','Suno AI Pricing','Free vs Pro vs Premier','/suno-ai-pricing'],
    ['🤖','ChatGPT Music Prompts','Better prompts with AI','/chatgpt-music-prompts'],
    ['🎮','AI Music for Games','Game soundtrack generation','/ai-music-for-games'],
    ['🎙️','AI Music for Podcasts','Intro, bed & sting music','/ai-music-for-podcasts'],
    ['📖','How to Use Suno AI','Complete beginner guide','/how-to-use-suno-ai'],
    ['💵','Make Money with Suno','YouTube monetisation guide','/how-to-make-money-suno-ai'],
    ['📋','All Tools Directory','Every RaagEngine page','/all-tools'],
  ];

  const renderLink = ([e,t,s,h]: string[]) => {
    return <a key={t} href={h} style={ls}>
          <span style={{ fontSize:'clamp(16px,3.5vw,19px)', flexShrink:0, marginTop:'1px' }}>{e}</span>
          <span style={{ display:'flex', flexDirection:'column', gap:'2px' }}>
            <b style={{ fontFamily:F, fontSize:'clamp(11px,2.5vw,13px)', fontWeight:700, color:'#1a1208', lineHeight:1.3 }}>{t}</b>
            <em style={{ fontFamily:F, fontSize:'clamp(9px,2vw,11px)', color:'#6b5c3e', fontStyle:'normal', lineHeight:1.3 }}>{s}</em>
          </span>
        </a>;
  };

  const ShowMoreBtn = ({ open, setOpen, count }: { open:boolean; setOpen:(v:boolean)=>void; count:number }) => (
    <button onClick={() => setOpen(!open)}
      style={{ display:'flex', alignItems:'center', gap:'6px', background:'none', border:'1px solid rgba(196,154,60,0.25)', borderRadius:'20px', padding:'6px 14px', cursor:'pointer', fontFamily:F, fontSize:'12px', fontWeight:600, color:'#c49a3c', marginTop:'4px', transition:'background .15s' }}
      onMouseOver={e=>(e.currentTarget.style.background='rgba(196,154,60,0.06)')}
      onMouseOut={e=>(e.currentTarget.style.background='')}>
      {open ? '↑ Show less' : `+ ${count} more`}
    </button>
  );

  return (
    <section className="re-sec re-music-links-grid" style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'clamp(16px,3vw,32px)', alignItems:'start' }}>
      <div>
        <div className="re-sec-kicker" style={{ textAlign:'left' }}>MUSIC STYLES</div>
        {STYLES_ALL.slice(0, stylesOpen ? 9 : 4).map(renderLink)}
        <ShowMoreBtn open={stylesOpen} setOpen={setStylesOpen} count={STYLES_ALL.length - 4} />
      </div>
      <div>
        <div className="re-sec-kicker" style={{ textAlign:'left' }}>FOR CREATORS</div>
        {CREATORS_ALL.slice(0, creatorsOpen ? 9 : 4).map(renderLink)}
        <ShowMoreBtn open={creatorsOpen} setOpen={setCreatorsOpen} count={CREATORS_ALL.length - 4} />
      </div>
    </section>
  );
}

// ── GeneratorPage — lives at /ai-music-generator ──────────────────────────────
function GeneratorPage({ onSignIn, user, usage, onAfterGenerate }: { onSignIn:()=>void; user:any; usage:Usage|null; onAfterGenerate:()=>void }) {
  const [mode, setMode] = useState<Mode>('feel');
  const [showInstructions, setShowInstructions] = useState(true);
  const F = "'Plus Jakarta Sans',sans-serif";
  const FS = "'Cormorant Garamond',Georgia,serif";

  return (
    <>
      {/* Page SEO meta injected at runtime */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context':'https://schema.org', '@type':'SoftwareApplication',
        'name':'RaagEngine AI Music Prompt Generator',
        'applicationCategory':'MusicApplication',
        'operatingSystem':'Web',
        'offers':{ '@type':'Offer', 'price':'0', 'priceCurrency':'USD', 'description':'Free unlimited generations' },
        'description':'AI music prompt generator for Suno AI, Udio, Stable Audio and 5 more platforms. Specialises in Indian classical ragas, YouTube SEO metadata, and binaural frequencies.',
      }) }} />

      {/* ── Generator hero — compact, mobile-first ── */}
      <div className="re-gen-hero" style={{ background:'linear-gradient(135deg,#1a0c04,#2c1608)', borderRadius:'14px', padding:'clamp(14px,3vw,32px) clamp(16px,4vw,44px)', marginBottom:'16px', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'linear-gradient(rgba(196,154,60,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(196,154,60,0.04) 1px,transparent 1px)', backgroundSize:'40px 40px' }} />
        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'10px' }}>
            <div>
              <h1 style={{ fontFamily:FS, fontSize:'clamp(20px,4vw,36px)', fontWeight:700, color:'#fff', margin:'0 0 6px', lineHeight:1.15 }}>
                AI Music Prompt Generator
              </h1>
              <p className="re-hero-sub" style={{ fontFamily:F, fontSize:'clamp(12px,1.8vw,14px)', color:'rgba(255,255,255,0.65)', margin:0, lineHeight:1.5 }}>
                Suno AI · Udio · Stable Audio · 5 more — free &amp; unlimited
              </p>
            </div>
          </div>
          <div className="re-hero-badges" style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginTop:'10px' }}>
            {['8 platforms','35+ ragas','YouTube SEO','No card'].map(t => (
              <span key={t} style={{ fontFamily:F, fontSize:'10px', fontWeight:600, color:'rgba(255,255,255,0.55)', background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.1)', padding:'3px 9px', borderRadius:'20px' }}>✓ {t}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Mode switcher ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'clamp(8px,2vw,14px)', marginBottom:'16px' }}>
        {([
          { id:'feel' as Mode, icon:'🎭', title:'Feel Mode', sub:'Emotion → complete package', disabled:false },
          { id:'describe' as any, icon:'💬', title:'Describe It', sub:'COMING SOON', disabled:true },
          { id:'expert' as Mode, icon:'🎛', title:'Expert Mode', sub:'Ragas · Binaural · Growth tools', disabled:false },
        ] as const).map(m => (
          <button key={m.id} disabled={m.disabled}
            onClick={() => { if (!m.disabled) { setMode(m.id as Mode); setShowInstructions(true); } }}
            style={{ background: (!m.disabled && mode===m.id) ? 'linear-gradient(135deg,#2c1810,#3a2414)' : '#fff', border: (!m.disabled && mode===m.id) ? '2px solid #c49a3c' : '1px solid rgba(196,154,60,0.2)', borderRadius:'12px', padding:'clamp(10px,2vw,16px) clamp(8px,1.5vw,14px)', cursor:m.disabled?'not-allowed':'pointer', opacity:m.disabled?0.45:1, textAlign:'left', transition:'all 0.2s', display:'flex', flexDirection:'column', gap:'4px' }}>
            <div style={{ fontSize:'clamp(18px,3vw,24px)', lineHeight:1 }}>{m.icon}</div>
            <div style={{ fontFamily:F, fontSize:'clamp(12px,2.5vw,14px)', fontWeight:700, color:(!m.disabled&&mode===m.id)?'#e8c87a':'#1a1208', marginTop:'4px', lineHeight:1.2 }}>{m.title}</div>
            <div style={{ fontFamily:F, fontSize:'clamp(10px,2vw,11px)', color:m.disabled?'#c49a3c':(!m.disabled&&mode===m.id)?'rgba(232,200,122,0.7)':'#6b5c3e', lineHeight:1.3, fontWeight:m.disabled?700:400 }}>{m.sub}</div>
          </button>
        ))}
      </div>

      {/* ── Badges ── */}
      <div className="re-gen-badges" style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginBottom:'20px', fontFamily:F }}>
        <span style={{ background:'#1a1208', color:'#faf7f2', fontSize:'11px', fontWeight:700, letterSpacing:'0.08em', padding:'5px 12px', borderRadius:'20px', textTransform:'uppercase' }}>Instrumental Only</span>
        <span style={{ background:'linear-gradient(135deg,#1a2a4a,#2a4a7a)', color:'#7eb8f7', fontSize:'11px', fontWeight:700, letterSpacing:'0.08em', padding:'5px 12px', borderRadius:'20px', textTransform:'uppercase' }}>+ Binaural</span>
        <span style={{ background:'linear-gradient(135deg,rgba(196,154,60,0.12),rgba(196,154,60,0.06))', border:'1px solid rgba(196,154,60,0.3)', color:'#a07840', fontSize:'11px', fontWeight:600, letterSpacing:'0.06em', padding:'5px 14px', borderRadius:'20px' }}>200+ Instruments · 35+ Ragas · YouTube SEO · Thumbnail Concepts</span>
      </div>

      {/* ── Generator ── */}
      <main className="re-main">
        {!user ? (
          <div className="re-signin-prompt">
            <h2>Sign in to start generating</h2>
            <p>Free &amp; unlimited · no card required.</p>
            <button className="re-btn-primary re-btn-lg" onClick={onSignIn}>Get started free</button>
          </div>
        ) : (
          <>
            <BilingualLabels mode={mode} />
            <GeneratorView key={mode} mode={mode} usage={usage} onAfterGenerate={onAfterGenerate} />
          </>
        )}
      </main>

      {/* ── Platform logos strip + How It Works — below hero on generator page ── */}
      <div className="re-gen-platform-strip" style={{ overflowX:'auto', scrollbarWidth:'none', margin:'12px 0 20px' } as any}>
        <div className="re-platform-strip" style={{ display:'grid', gridTemplateColumns:'repeat(8,1fr)', gap:'clamp(6px,1.2vw,10px)', minWidth:'560px' }}>
          {PLATFORMS.map(p => (
            <a key={p.name} href={p.href}
              style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'6px', padding:'clamp(10px,2vw,16px) 6px', background:'#fff', border:'1px solid rgba(196,154,60,0.14)', borderRadius:'14px', textDecoration:'none', transition:'all .2s', textAlign:'center' }}
              onMouseOver={e=>{e.currentTarget.style.borderColor=p.badge;e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow=`0 6px 16px ${p.badge}22`;}}
              onMouseOut={e=>{e.currentTarget.style.borderColor='rgba(196,154,60,0.14)';e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='';}}>
              <div style={{ width:'clamp(28px,4vw,40px)', height:'clamp(28px,4vw,40px)', borderRadius:'10px', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', background:`${p.badge}12`, flexShrink:0, padding:'3px' }}>
                <PlatformLogo domain={p.domain} badge={p.badge} size={34} />
              </div>
              <div style={{ fontFamily:F, fontSize:'clamp(9px,1.3vw,12px)', fontWeight:700, color:'#1a1208', lineHeight:1.2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'100%' }}>{p.name}</div>
              <span style={{ fontFamily:F, fontSize:'9px', color:p.badge, fontWeight:700 }}>Prompts →</span>
            </a>
          ))}
        </div>
      </div>

      {/* ── How it works — 3 steps visual ── */}
      <div className="re-gen-hiw" style={{ background:'linear-gradient(135deg,#fdf9f2,#faf3e8)', border:'1px solid rgba(196,154,60,0.15)', borderRadius:'16px', padding:'clamp(20px,3vw,32px)', marginBottom:'24px' }}>
        <div style={{ fontSize:'10px', fontWeight:700, letterSpacing:'2px', color:'#c49a3c', textTransform:'uppercase', fontFamily:F, textAlign:'center', marginBottom:'16px' }}>HOW IT WORKS</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'clamp(12px,3vw,28px)', position:'relative' }}>
          {[
            { n:'1', icon:'🎭', title:'Choose your mode', body:'Feel Mode: pick genre + mood. Expert Mode: choose raga, BPM, binaural frequency individually.' },
            { n:'2', icon:'⚡', title:'Select platforms', body:"Tick any of the 8 AI music platforms. RaagEngine formats each prompt to that platform's exact syntax." },
            { n:'3', icon:'🎵', title:'Copy & create', body:'Get 8 prompts, a YouTube SEO pack, DistroKid metadata, and a thumbnail concept — in one click.' },
          ].map((s, i, arr) => (
            <div key={s.n} style={{ display:'flex', flexDirection:'column', gap:'8px', alignItems:'flex-start', position:'relative' }}>
              {i < arr.length - 1 && (
                <div style={{ position:'absolute', top:'18px', right:'-14px', width:'28px', height:'2px', background:'linear-gradient(90deg,rgba(196,154,60,0.4),transparent)', display:'none' }} className="re-step-arrow" />
              )}
              <div style={{ width:'36px', height:'36px', borderRadius:'50%', background:'linear-gradient(135deg,#c49a3c,#e8b84b)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px', fontWeight:800, color:'#1a1208', fontFamily:F, flexShrink:0 }}>{s.n}</div>
              <div style={{ fontSize:'clamp(18px,3.5vw,24px)', lineHeight:1 }}>{s.icon}</div>
              <div style={{ fontFamily:F, fontSize:'clamp(12px,2vw,14px)', fontWeight:700, color:'#1a1208', lineHeight:1.3 }}>{s.title}</div>
              <div style={{ fontFamily:F, fontSize:'clamp(11px,1.8vw,13px)', color:'#6b5c3e', lineHeight:1.6 }}>{s.body}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── SEO text content (for Google) ── */}
      <section className="re-gen-seo-section" style={{ marginTop:'48px', padding:'32px 0', borderTop:'1px solid rgba(196,154,60,0.1)' }}>
        <h2 style={{ fontFamily:FS, fontSize:'clamp(20px,3vw,30px)', fontWeight:700, color:'#1a1208', marginBottom:'14px' }}>
          How to use the AI Music Prompt Generator
        </h2>
        <p style={{ fontFamily:F, fontSize:'14px', color:'#6b5c3e', lineHeight:1.8, marginBottom:'16px', maxWidth:'720px' }}>
          RaagEngine generates production-ready style prompts for the world's leading AI music platforms. Choose <strong>Feel Mode</strong> to describe your emotion and scene — the generator handles everything. Choose <strong>Expert Mode</strong> for granular control: select your raga, instruments, BPM, binaural frequency, and arrangement style individually.
        </p>
        <p style={{ fontFamily:F, fontSize:'14px', color:'#6b5c3e', lineHeight:1.8, marginBottom:'16px', maxWidth:'720px' }}>
          Every generation includes <strong>8 platform-specific prompts</strong> (Suno AI, Udio, Stable Audio, Mureka, Beatoven.ai, Soundraw, Loudly, MusicGen), a <strong>YouTube SEO title and description</strong>, DistroKid release metadata, and a thumbnail visual concept — all in one click.
        </p>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:'12px', marginTop:'24px' }}>
          {[
            { title:'AI Prompt Generator', href:'/ai-music-prompt-generator', desc:'Best AI music prompt generator 2026' },
            { title:'Suno AI Prompts', href:'/suno', desc:'Style tags and custom mode guide' },
            { title:'Udio Prompts', href:'/udio', desc:'Natural language prompt guide' },
            { title:'Indian Classical AI', href:'/indian-classical-music-ai', desc:'35+ ragas, authentic raga science' },
            { title:'YouTube Music Channel', href:'/youtube-music', desc:'Build and monetise with AI music' },
            { title:'Royalty Free AI Music', href:'/royalty-free-ai-music', desc:'Copyright-free music guide' },
            { title:'Best AI Music 2026', href:'/best-ai-music-generator-2026', desc:'Full platform comparison' },
            { title:'Suno AI Pricing', href:'/suno-ai-pricing', desc:'Free vs Pro vs Premier guide' },
          ].map(l => (
            <a key={l.title} href={l.href} style={{ background:'#faf7f0', border:'1px solid rgba(196,154,60,0.15)', borderRadius:'10px', padding:'14px 16px', textDecoration:'none', transition:'border-color .2s' }}
              onMouseOver={e=>(e.currentTarget.style.borderColor='#c49a3c')}
              onMouseOut={e=>(e.currentTarget.style.borderColor='rgba(196,154,60,0.15)')}>
              <div style={{ fontFamily:F, fontSize:'13px', fontWeight:700, color:'#1a1208', marginBottom:'4px' }}>{l.title}</div>
              <div style={{ fontFamily:F, fontSize:'11px', color:'#7a6355' }}>{l.desc}</div>
            </a>
          ))}
        </div>
      </section>
    </>
  );
}

function HomePage({ onSignIn, user, usage, onAfterGenerate }: { onSignIn:()=>void; user:any; usage:Usage|null; onAfterGenerate:()=>void }) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const navigate = useNavigate();

  const F = "'Plus Jakarta Sans',sans-serif";
  const FS = "'Cormorant Garamond',Georgia,serif";
  const { videos: ytVideos, loading: ytLoading } = useYouTubeVideos();
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);

  return (
    <>
      <FAQSchema />

      {/* ── 1. HERO ── */}
      <HeroBanner onSignIn={onSignIn} user={user} onNavigateGenerate={() => navigate('/')} />

      {/* ── 1b. PLATFORM TILES — 8 in a row directly below hero ── */}
      <div style={{ overflowX:'auto', scrollbarWidth:'none', marginTop:'12px', marginBottom:'8px' } as any}>
        <div className="re-platform-strip" style={{ display:'grid', gridTemplateColumns:'repeat(8,1fr)', gap:'clamp(6px,1.2vw,12px)', minWidth:'560px' }}>
          {PLATFORMS.map(p => (
            <a key={p.name} href={p.href}
              style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'8px', padding:'clamp(12px,2vw,18px) 8px', background:'#fff', border:'1px solid rgba(196,154,60,0.14)', borderRadius:'14px', textDecoration:'none', transition:'all .2s', textAlign:'center' }}
              onMouseOver={e=>{e.currentTarget.style.borderColor=p.badge;e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow=`0 6px 20px ${p.badge}22`;}}
              onMouseOut={e=>{e.currentTarget.style.borderColor='rgba(196,154,60,0.14)';e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='';}}>
              <div style={{ width:'clamp(36px,5vw,48px)', height:'clamp(36px,5vw,48px)', borderRadius:'12px', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', background:`${p.badge}12`, flexShrink:0, padding:'4px' }}>
                <PlatformLogo domain={p.domain} badge={p.badge} size={40} />
              </div>
              <div style={{ fontFamily:F, fontSize:'clamp(10px,1.4vw,13px)', fontWeight:700, color:'#1a1208', lineHeight:1.25, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'100%' }}>{p.name}</div>
              <div style={{ fontFamily:F, fontSize:'clamp(9px,1.1vw,11px)', color:'#8a7055', lineHeight:1.3 }}>{p.sub}</div>
              <span style={{ fontFamily:F, fontSize:'10px', color:p.badge, fontWeight:700 }}>Prompts →</span>
            </a>
          ))}
        </div>
      </div>

      {/* ── 2. VALUE PROPOSITION BANNER ─────────────────────────────────────── */}
      <div className="re-value-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(min(220px,100%),1fr))', gap:'clamp(8px,1.5vw,12px)', marginBottom:'36px', marginTop:'10px' }}>
        {[
          { icon:'🪔', color:'#c49a3c', colorBg:'rgba(196,154,60,0.08)', title:'Indian Classical depth', body:'35+ ragas · 200+ instruments · real raga science. No other AI prompt tool goes this deep.' },
          { icon:'▶',  color:'#dc2626', colorBg:'rgba(220,38,38,0.07)',   title:'YouTube SEO built-in',   body:'Every generation includes an SEO title, description, tags and thumbnail concept.' },
          { icon:'⚡', color:'#2563eb', colorBg:'rgba(37,99,235,0.07)',   title:'8 platforms. One click.', body:'Suno, Udio, Stable Audio, Mureka, Beatoven, Soundraw, Loudly, MusicGen — all at once.' },
          { icon:'🆓', color:'#16a34a', colorBg:'rgba(22,163,74,0.07)',   title:'Free &amp; unlimited',     body:'No credit card, no limits, no subscription. Generate as much as you want, forever.' },
        ].map(c => (
          <div key={c.title} style={{ background:'#fff', border:'1px solid rgba(196,154,60,0.13)', borderRadius:'14px', padding:'clamp(14px,2.5vw,22px)', display:'flex', gap:'14px', alignItems:'flex-start' }}>
            <div style={{ width:'38px', height:'38px', borderRadius:'10px', background:c.colorBg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'18px', flexShrink:0 }}>{c.icon}</div>
            <div>
              <div style={{ fontFamily:F, fontSize:'clamp(12px,2vw,14px)', fontWeight:700, color:'#1a1208', marginBottom:'5px', lineHeight:1.3 }}>{c.title}</div>
              <div style={{ fontFamily:F, fontSize:'clamp(11px,1.6vw,13px)', color:'#7a6355', lineHeight:1.55 }}>{c.body}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── 4. HOW IT WORKS ──────────────────────────────────────────────────── */}
      <section style={{ background:'linear-gradient(135deg,#231508 0%,#3a2810 100%)', borderRadius:'16px', padding:'clamp(28px,5vw,52px) clamp(24px,5vw,52px)', marginBottom:'48px' }}>
        <div style={{ textAlign:'center', marginBottom:'32px' }}>
          <div style={{ fontSize:'11px', fontWeight:700, letterSpacing:'2px', color:'#c49a3c', textTransform:'uppercase', fontFamily:F, marginBottom:'8px' }}>HOW IT WORKS</div>
          <h2 style={{ fontFamily:FS, fontSize:'clamp(22px,3.5vw,36px)', fontWeight:700, color:'#fff', margin:0 }}>From feeling to finished prompt in 60 seconds</h2>
        </div>
        <div className="re-hiw-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(min(200px,100%),1fr))', gap:'clamp(16px,3vw,28px)' }}>
          {[
            { step:'1', icon:'🎭', title:'Choose your mode', body:'Feel Mode: pick a genre, mood, and activity. Expert Mode: choose ragas, instruments, BPM, and binaural frequency.' },
            { step:'2', icon:'⚡', title:'Select platforms', body:'Tick any or all 8 AI music platforms. RaagEngine formats each prompt to that platform\'s exact syntax.' },
            { step:'3', icon:'🎵', title:'Generate & copy', body:'Get platform-specific prompts, a YouTube SEO pack, DistroKid metadata, and a thumbnail concept — all at once.' },
          ].map(s => (
            <div key={s.step} style={{ display:'flex', flexDirection:'column', gap:'12px', alignItems:'flex-start' }}>
              <div style={{ width:'36px', height:'36px', borderRadius:'50%', background:'linear-gradient(135deg,#c49a3c,#e8b84b)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px', fontWeight:800, color:'#1a1208', fontFamily:F, flexShrink:0 }}>{s.step}</div>
              <div style={{ fontSize:'clamp(18px,3vw,26px)', lineHeight:1 }}>{s.icon}</div>
              <h3 style={{ fontFamily:F, fontSize:'clamp(13px,2.5vw,16px)', fontWeight:700, color:'#e8c87a', margin:0 }}>{s.title}</h3>
              <p style={{ fontFamily:F, fontSize:'clamp(11px,2vw,13px)', color:'rgba(255,255,255,0.7)', lineHeight:1.6, margin:0 }}>{s.body}</p>
            </div>
          ))}
        </div>
        <div style={{ textAlign:'center', marginTop:'32px' }}>
          <button onClick={user ? () => navigate('/') : onSignIn} style={{ background:'linear-gradient(135deg,#c49a3c,#e8b84b)', color:'#1a1208', border:'none', borderRadius:'28px', padding:'13px 28px', fontSize:'14px', fontWeight:700, cursor:'pointer', fontFamily:F }}>
            {user ? '✦ Open Generator →' : 'Start free — 25 generations →'}
          </button>
        </div>
      </section>

      {/* Platform tiles now shown directly below hero — section 1b */}

      {/* ── 6. YOUTUBE CHANNEL SECTION ───────────────────────────────────────── */}
      <section className="re-sec" id="videos" style={{ background:'linear-gradient(180deg,#fff 0%,#fdf9f2 100%)', borderRadius:'20px', padding:'clamp(28px,4vw,48px)', border:'1px solid rgba(196,154,60,0.12)' }}>
        <div className="re-sec-kicker">WATCH &amp; LEARN</div>
        <h2 className="re-sec-h" style={{ fontFamily:FS, fontSize:'clamp(22px,3vw,38px)', fontWeight:700, color:'#1a1208', marginBottom:'6px' }}>
          See RaagEngine in action
        </h2>
        <p style={{ fontFamily:F, fontSize:'13px', color:'#6b5c3e', textAlign:'center', marginBottom:'28px', lineHeight:1.6 }}>
          Tutorials · Prompt walkthroughs · YouTube channel growth tips · Indian classical deep-dives
        </p>

        {/* ── Active player ── */}
        {activeVideoId && (
          <div style={{ marginBottom:'20px', borderRadius:'14px', overflow:'hidden', border:'2px solid rgba(196,154,60,0.3)', background:'#000', boxShadow:'0 12px 48px rgba(26,18,8,0.25)' }}>
            <div style={{ position:'relative', paddingBottom:'56.25%', height:0 }}>
              <iframe
                key={activeVideoId}
                src={`https://www.youtube-nocookie.com/embed/${activeVideoId}?autoplay=1&rel=0&modestbranding=1`}
                title="RaagEngine YouTube video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', border:'none' }}
              />
            </div>
          </div>
        )}

        {/* ── Thumbnail grid ── */}
        {ytLoading ? (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(min(200px,45vw),1fr))', gap:'12px', marginBottom:'24px' }}>
            {YT_PINNED_IDS.map(id => (
              <div key={id} style={{ borderRadius:'10px', overflow:'hidden', background:'#f0e8d8', aspectRatio:'16/9', animation:'re-pulse 1.5s infinite' }} />
            ))}
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(min(200px,45vw),1fr))', gap:'clamp(8px,2vw,14px)', marginBottom:'24px' }}>
            {ytVideos.map(v => (
              <button
                key={v.id}
                onClick={() => setActiveVideoId(activeVideoId === v.id ? null : v.id)}
                style={{ background:'none', border:'none', padding:0, cursor:'pointer', textAlign:'left', borderRadius:'10px', overflow:'hidden', outline:'none', position:'relative' }}
                aria-label={`Play: ${v.title}`}
              >
                {/* Thumbnail */}
                <div style={{ position:'relative', paddingBottom:'56.25%', background:'#1a1208', borderRadius:'10px', overflow:'hidden', border: activeVideoId === v.id ? '2px solid #c49a3c' : '2px solid transparent', transition:'border-color 0.2s, transform 0.2s', transform: activeVideoId === v.id ? 'scale(1.02)' : 'scale(1)' }}>
                  <img
                    src={v.thumbnail}
                    alt={v.title}
                    loading="lazy"
                    style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover', filter: activeVideoId === v.id ? 'brightness(0.7)' : 'brightness(0.88)' }}
                  />
                  {/* Play overlay */}
                  <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <div style={{ width:'clamp(36px,8vw,48px)', height:'clamp(36px,8vw,48px)', borderRadius:'50%', background: activeVideoId === v.id ? '#c49a3c' : 'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', transition:'background 0.2s', backdropFilter:'blur(2px)' }}>
                      <div style={{ width:0, height:0, borderTop:'6px solid transparent', borderBottom:'6px solid transparent', borderLeft:'10px solid #fff', marginLeft:'2px' }} />
                    </div>
                  </div>
                </div>
                {/* Title */}
                {v.title && (
                  <div style={{ fontFamily:F, fontSize:'clamp(10px,2vw,12px)', fontWeight:600, color:'#1a1208', lineHeight:1.35, padding:'7px 2px 0', textOverflow:'ellipsis', overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
                    {v.title}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* ── Channel CTA row ── */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'clamp(10px,2vw,16px)', flexWrap:'wrap' }}>
          <a href={YT_CHANNEL_HANDLE} target="_blank" rel="noopener noreferrer"
            style={{ display:'inline-flex', alignItems:'center', gap:'8px', background:'#ff0000', color:'#fff', textDecoration:'none', padding:'9px 20px', borderRadius:'24px', fontSize:'13px', fontWeight:700, fontFamily:F }}>
            <span style={{ fontSize:'15px' }}>▶</span> Subscribe on YouTube
          </a>
          <span style={{ fontFamily:F, fontSize:'12px', color:'#8c7a5e' }}>New videos every week</span>
        </div>
      </section>

      {/* ── 7. MUSIC STYLES + FOR CREATORS (4 each + expandable) ─────────────── */}
      <MusicLinksSection navigate={navigate} />

      {/* ── 8. BLOG PREVIEW — auto-populated from blogPosts (latest 4) ────── */}
      <section className="re-sec">
        <div className="re-sec-kicker">FROM THE BLOG</div>
        <h2 className="re-sec-h" style={{ fontFamily:FS, fontSize:'clamp(20px,3vw,34px)', fontWeight:700, color:'#1a1208', marginBottom:'20px' }}>Latest from RaagEngine</h2>
        <div className="re-blog-grid" style={{ gridTemplateColumns:'repeat(auto-fill,minmax(min(260px,100%),1fr))', gap:'clamp(8px,2vw,14px)' }}>
          {[...blogPosts].sort((a,b) => b.isoDate.localeCompare(a.isoDate)).slice(0,4).map(b => (
            <a key={b.id} href={`/blog/${b.slug}/`} className="re-blog-card" style={{ textDecoration:'none', background:'none', border:'1px solid rgba(196,154,60,0.15)', textAlign:'left', width:'100%', display:'block', color:'inherit' }}>
              <div className="re-blog-kicker">{b.category.toUpperCase()}</div>
              <div className="re-blog-t">{b.title}</div>
              <div className="re-blog-s">{b.desc}</div>
              <div style={{ fontFamily:F, fontSize:'10px', color:'#a09080', marginTop:'8px' }}>{b.date}</div>
            </a>
          ))}
        </div>
        <div className="re-blog-more">
          <a href="/blog/" style={{ color:'#c49a3c', textDecoration:'none', fontSize:'13px', fontWeight:600, fontFamily:F }}>Read all articles →</a>
        </div>
      </section>

      {/* ── 9. FAQ ───────────────────────────────────────────────────────────── */}
      <section className="re-sec" id="faq">
        <div className="re-sec-kicker">FAQ</div>
        <h2 className="re-sec-h" style={{ fontFamily:FS, fontSize:'clamp(22px,3vw,36px)', fontWeight:700, color:'#1a1208', marginBottom:'24px' }}>Frequently asked questions</h2>
        <div style={{ maxWidth:'720px', margin:'0 auto' }}>
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} style={{ borderBottom:'1px solid rgba(196,154,60,0.15)', overflow:'hidden' }}>
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                style={{ width:'100%', background:'none', border:'none', cursor:'pointer', padding:'16px 0', display:'flex', justifyContent:'space-between', alignItems:'center', gap:'12px', textAlign:'left' }}>
                <span style={{ fontFamily:F, fontSize:'clamp(13px,2.5vw,15px)', fontWeight:700, color:'#1a1208', lineHeight:1.4 }}>{item.q}</span>
                <span style={{ fontSize:'18px', color:'#c49a3c', flexShrink:0, transition:'transform 0.25s', transform:openFaq === i ? 'rotate(45deg)' : 'rotate(0deg)', display:'inline-block' }}>+</span>
              </button>
              {openFaq === i && (
                <div style={{ fontFamily:F, fontSize:'clamp(12px,2vw,14px)', color:'#5c5240', lineHeight:1.7, paddingBottom:'16px' }}>{item.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── 10. FINAL CTA ─────────────────────────────────────────────────────── */}
      <section className="re-sec">
        <div style={{ background:'linear-gradient(135deg,#1a0c04,#2c1608)', borderRadius:'20px', padding:'clamp(32px,5vw,56px)', textAlign:'center', border:'1px solid rgba(196,154,60,0.2)' }}>
          <div style={{ fontSize:'11px', fontWeight:700, letterSpacing:'2.5px', color:'#c49a3c', textTransform:'uppercase', fontFamily:F, marginBottom:'12px' }}>Start Free Today</div>
          <h2 style={{ fontFamily:FS, fontSize:'clamp(24px,4vw,44px)', fontWeight:700, color:'#fff', margin:'0 0 14px', lineHeight:1.2 }}>
            Ready to generate your first prompt?
          </h2>
          <p style={{ fontFamily:F, fontSize:'clamp(13px,2vw,16px)', color:'rgba(255,255,255,0.65)', margin:'0 auto 28px', lineHeight:1.7, maxWidth:'500px' }}>
            Free &amp; unlimited · all 8 platforms · no credit card
          </p>
          <button onClick={user ? () => navigate('/') : onSignIn}
            style={{ display:'inline-flex', alignItems:'center', gap:'10px', background:'linear-gradient(135deg,#c49a3c,#e8b84b)', color:'#1a1208', border:'none', cursor:'pointer', padding:'14px 36px', borderRadius:'32px', fontSize:'16px', fontWeight:800, fontFamily:F, boxShadow:'0 6px 24px rgba(196,154,60,0.4)', transition:'transform .15s,box-shadow .15s' }}
            onMouseOver={(e:any)=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 10px 32px rgba(196,154,60,0.5)';}}
            onMouseOut={(e:any)=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='0 6px 24px rgba(196,154,60,0.4)';}}>
            🎵 {user ? 'Open the Generator' : 'Start Free — Unlimited'}
          </button>
          <div style={{ display:'flex', justifyContent:'center', gap:'clamp(12px,3vw,28px)', marginTop:'24px', flexWrap:'wrap' }}>
            {['Suno AI','Udio','Stable Audio','Mureka','+ 4 more'].map(t => (
              <span key={t} style={{ fontFamily:F, fontSize:'12px', color:'rgba(255,255,255,0.4)', fontWeight:500 }}>{t}</span>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}


function BlogRedirect() {
  // Blog posts and the blog index are served as static HTML (with full SEO + schema).
  // If a client-side route ever resolves here, hard-redirect to the static page so the
  // visitor gets the real HTML (single source of truth — no duplicated React content).
  useEffect(() => {
    const path = window.location.pathname;
    const dest = path.endsWith('/') ? path : path + '/';
    window.location.replace(dest);
  }, []);
  return null;
}

function AppInner() {
  const { user, loading, signOut } = useAuth();
  const [showSignIn, setShowSignIn] = useState(false);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(false);

  useEffect(() => {
    if (window.location.search.includes('checkout=success')) {
      setShowCheckoutSuccess(true);
      window.history.replaceState({}, '', '/');
    }
  }, []);

  useEffect(() => { if (user) fetchUsage(); else setUsage(null); }, [user]);

  async function fetchUsage() {
    if (!user) return;
    const { data } = await supabase.from('entitlements').select('*').eq('user_id', user.id).single();
    const plan = (data?.plan ?? 'free') as 'free' | 'paid';
    const used = plan === 'paid' ? (data?.paid_used_this_period ?? 0) : (data?.free_used ?? 0);
    const lim = data?.limit ?? (plan === 'paid' ? 100 : FREE_LIMIT);
    setUsage({ plan, gens_used: used, limit: lim, isMaster: !!data?.is_master, paddle_plan: data?.paddle_plan });
  }

  return (
    <BrowserRouter>
      <SignInAutoTrigger onSignIn={() => setShowSignIn(true)} />
      {showCheckoutSuccess && <CheckoutSuccessBanner onDismiss={() => setShowCheckoutSuccess(false)} fetchUsage={fetchUsage} />}
      <Layout onSignIn={() => setShowSignIn(true)} user={user} loading={loading} usage={usage} signOut={signOut}>
        <Routes>
          <Route path="/" element={<GeneratorPage onSignIn={() => setShowSignIn(true)} user={user} usage={usage} onAfterGenerate={() => setTimeout(fetchUsage, 600)} />} />
          <Route path="/ai-music-generator" element={<GeneratorPage onSignIn={() => setShowSignIn(true)} user={user} usage={usage} onAfterGenerate={() => setTimeout(fetchUsage, 600)} />} />
          <Route path="/home" element={<HomePage onSignIn={() => setShowSignIn(true)} user={user} usage={usage} onAfterGenerate={() => setTimeout(fetchUsage, 600)} />} />
          <Route path="/blog" element={<BlogRedirect />} />
          <Route path="/blog/:slug" element={<BlogRedirect />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </Layout>
      <SignInModal open={showSignIn} onClose={() => setShowSignIn(false)} />
    </BrowserRouter>
  );
}

export default function App() {
  return <AuthProvider><AppInner /></AuthProvider>;
}
